import { BadgeList } from '@/components/badges'
import { useBadges } from '@/lib/verification'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { Crosshair, MapPin, Loader2 } from 'lucide-react'
import { BrandMark, BRAND_NAME } from '@/components/brand'
import { LanguageMenu, useT } from '@/i18n'
import { AnyPlacePicker } from '@/components/any-place-picker'
import { SkeletonRows } from '@/components/rm'
import { useAuth } from '@/hooks/use-auth'
import { peso } from '@/lib/ph'
import { useCategories } from '@/lib/listing'
import { devicePoint, loadLastPlace, placeByCode, saveLastPlace, useSearchListings, type AnyPlace, type Point } from '@/lib/search'
import { cn } from '@/lib/utils'

// Spec 004. Public: a visitor can search without an account. Phone first.
type Search = { category?: string; place?: string }

export const Route = createFileRoute('/search')({
  validateSearch: (s: Record<string, unknown>): Search => ({
    category: typeof s.category === 'string' && /^[a-z]+$/.test(s.category) ? s.category : undefined,
    place: typeof s.place === 'string' && /^[0-9]{9,10}$/.test(s.place) ? s.place : undefined,
  }),
  component: SearchPage,
})

function SearchPage() {
  const { t, lang } = useT()
  const { session } = useAuth()
  const navigate = useNavigate({ from: '/search' })
  const { category, place: placeCode } = Route.useSearch()
  const cats = useCategories()
  const [place, setPlace] = useState<AnyPlace | null>(null)
  const [point, setPoint] = useState<Point | null>(null)
  const [locating, setLocating] = useState(false)
  const [nearFailed, setNearFailed] = useState(false)

  // The place in the address wins; otherwise the last one this person searched.
  useEffect(() => {
    let live = true
    ;(async () => {
      const code = placeCode ?? (await loadLastPlace())
      if (!code) return
      const p = await placeByCode(code)
      if (live && p) { setPlace(p); if (!placeCode) navigate({ search: (s: Search) => ({ ...s, place: p.code }), replace: true }) }
    })()
    return () => { live = false }
  }, [placeCode, navigate])

  const pick = (p: AnyPlace) => {
    setPlace(p)
    void saveLastPlace(p.code)
    navigate({ search: (s: Search) => ({ ...s, place: p.code }), replace: true })
  }
  const setCategory = (c: string | undefined) => navigate({ search: (s: Search) => ({ ...s, category: c }), replace: true })
  const nearMe = async () => {
    setLocating(true); setNearFailed(false)
    const p = await devicePoint()
    setLocating(false)
    if (p) setPoint(p); else setNearFailed(true)
  }

  const results = useSearchListings(place?.code ?? null, category ?? null, point)
  const badges = useBadges((results.data ?? []).map((r) => r.slug))
  const placeName = place ? place.name : ''

  return (
    <div className="min-h-screen bg-canvas">
      <header className="sticky top-0 z-20 border-b border-token-subtle bg-token-surface/95 pt-[env(safe-area-inset-top,0px)] backdrop-blur">
        <div className="mx-auto flex h-14 max-w-3xl items-center gap-2 px-4">
          <Link to="/" className="flex items-center gap-2"><BrandMark className="h-8 w-8" /><span className="hidden font-display text-[17px] font-bold text-token-primary min-[400px]:inline">{BRAND_NAME}</span></Link>
          <div className="ml-auto flex items-center gap-2">
            <LanguageMenu />
            {!session && <Link to="/login" className="whitespace-nowrap text-[13px] font-semibold text-brand hover:underline">{t('search.signIn')}</Link>}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl space-y-4 px-4 py-5">
        <h1 className="font-display text-[22px] font-bold tracking-[-0.02em] text-token-primary">{t('search.title')}</h1>

        <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1">
          <Chip on={!category} onClick={() => setCategory(undefined)}>{t('search.allCategories')}</Chip>
          {(cats.data ?? []).map((c) => (
            <Chip key={c.slug} on={category === c.slug} onClick={() => setCategory(c.slug)}>{lang === 'fil' ? c.name_fil : c.name_en}</Chip>
          ))}
        </div>

        <section className="space-y-2 rounded-[16px] border border-token-subtle bg-token-surface p-4">
          <p className="text-[13px] font-semibold text-token-secondary">{t('search.where')}</p>
          {place
            ? <div className="flex items-center justify-between gap-3 rounded-[12px] border border-token-subtle px-3 py-2.5">
                <span className="flex min-w-0 items-center gap-2"><MapPin className="h-4 w-4 flex-none text-brand" />
                  <span className="min-w-0"><span className="block truncate text-[15px] font-semibold text-token-primary">{place.name}</span>
                    {place.parent && <span className="block truncate text-[12px] text-token-secondary">{place.parent}</span>}</span>
                </span>
                <button type="button" className="flex-none text-[13px] font-semibold text-brand hover:underline" onClick={() => setPlace(null)}>{t('search.change')}</button>
              </div>
            : <AnyPlacePicker placeholder={t('search.wherePlaceholder')} onPick={pick} />}
          <button type="button" onClick={nearMe} disabled={locating}
            className={cn('inline-flex items-center gap-1.5 text-[13px] font-semibold', point ? 'text-token-success' : 'text-brand hover:underline')}>
            {locating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Crosshair className="h-4 w-4" />}
            {point ? t('search.nearOn') : t('search.near')}
          </button>
          {nearFailed && <p role="alert" className="text-[12.5px] text-token-warning">{t('search.nearFailed')}</p>}
        </section>

        {!place && !point && <p className="px-1 text-[13.5px] text-token-secondary">{t('search.pickPlace')}</p>}

        {(place || point) && (results.isLoading
          ? <SkeletonRows count={4} />
          : (results.data ?? []).length === 0
            ? <div className="rounded-[16px] border border-token-subtle bg-token-surface p-5 text-center">
                <p className="text-[15px] font-semibold text-token-primary">{t('search.none', { place: placeName || t('search.nearOn') })}</p>
                <p className="mt-1 text-[13px] text-token-secondary">{t('search.noneHint')}</p>
              </div>
            : <>
                <p className="px-1 text-[13px] text-token-secondary">{t('search.count', { n: results.data!.length, place: placeName || t('search.nearOn').toLowerCase() })}</p>
                <ul className="space-y-3">
                  {results.data!.map((r) => (
                    <li key={r.slug}>
                      <Link to="/p/$slug" params={{ slug: r.slug }} className="flex gap-3 rounded-[16px] border border-token-subtle bg-token-surface p-4 transition-colors hover:border-token-strong">
                        {r.logo
                          ? <img src={r.logo} alt="" className="h-14 w-14 flex-none rounded-[12px] border border-token-subtle object-contain p-0.5" />
                          : <span className="flex h-14 w-14 flex-none items-center justify-center rounded-[12px] font-display text-[22px] font-bold text-brand-fg" style={{ background: r.accent_color || '#0038a8' }}>{r.business_name.slice(0, 1).toUpperCase()}</span>}
                        <span className="min-w-0 flex-1">
                          <span className="flex items-start justify-between gap-2">
                            <span className="truncate text-[15.5px] font-bold text-token-primary">{r.business_name}</span>
                            <span className="flex-none text-[13.5px] font-bold tabular-nums text-token-primary">{r.quote_only ? t('search.quote') : t('search.from', { price: peso(r.from_price, { whole: true }) })}</span>
                          </span>
                          <span className="mt-0.5 line-clamp-2 block text-[13px] text-token-secondary">{r.headline}</span>
                          <BadgeList b={badges.data?.get(r.slug)} className="mt-1.5" />
                          <span className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-token-muted">
                            {r.base_city && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{t('search.basedIn', { place: r.base_city })}</span>}
                            {r.distance_km != null && <span className="font-semibold text-token-secondary">{t('search.away', { km: r.distance_km })}</span>}
                          </span>
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </>)}
      </main>
    </div>
  )
}

function Chip({ on, onClick, children }: { on: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" aria-pressed={on} onClick={onClick}
      className={cn('flex-none whitespace-nowrap rounded-full border px-3.5 py-1.5 text-[13px] font-semibold transition-colors',
        on ? 'border-brand bg-brand text-brand-fg' : 'border-token-subtle bg-token-surface text-token-secondary hover:text-token-primary')}>
      {children}
    </button>
  )
}
